import { HostError } from '@finxone-platform/shared/app-sdk';
import { ConfirmationClaimType } from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import { ConfirmationDeclinedError } from '../../../../lib/services/overlay-confirmation-service/confirmation-declined-error';
import { OverlayConfirmationService } from '../../../../lib/services/overlay-confirmation-service/overlay-confirmation.service';
import { ProjectSettingsState } from '../../../../lib/state/project-settings.state';
import { RoleState } from '../../../../lib/state/role.state';

/**
 * Outcome of a completed user confirmation (SCA). `token` is the Keycloak
 * confirmation access token minted by /confirmation-code
 * (KeycloakCustomService.sendConfirmation), which flows that attach it send in
 * the `confirmation` HTTP header (see AccountService.addBeneficiary). Empty
 * when the flow confirmed without minting a caller-visible token.
 */
export interface ConfirmationOutcome {
  token: string;
}

/**
 * Runs the platform confirmation UX (SMS OTP / PIN / biometric ...) over the
 * current page for one confirmation-gated widget operation. Contract:
 *
 * - MUST NOT unmount the page hosting the widget iframe — the widget's
 *   api.call promise suspends across the whole confirmation.
 * - Resolves with the ConfirmationOutcome once the user confirms.
 * - Rejects with HostError('confirmation-declined', 'confirmation-declined: ...')
 *   when the user dismisses the confirmation or it times out.
 */
export interface ConfirmationRunner {
  run(claimType: ConfirmationClaimType, operation: string): Promise<ConfirmationOutcome>;
}

export interface ConfirmationGatedOperation {
  /** Keycloak confirmation claim the native flow confirms for this operation. */
  claimType: ConfirmationClaimType;
  /** Project-settings check mirroring the native flow's gate. */
  isConfirmationRequired(store: Store): boolean;
}

/**
 * Native parity (beneficiaryRequiresConfirmation, add-beneficiary.ts): the
 * in-app beneficiaryConfirmationEnabled flag with the persona-specific
 * override taking precedence when the active role has its own settings block.
 */
function isBeneficiaryConfirmationRequired(store: Store): boolean {
  const settings = store.selectSnapshot(ProjectSettingsState.getProjectSettings);
  const role = store.selectSnapshot(RoleState.getRole)?.role;
  const personaSettings = settings?.projectConfig?.personaSpecificSettings;
  if (role && personaSettings && Object.prototype.hasOwnProperty.call(personaSettings, role)) {
    return !!personaSettings[role]?.authentication?.inApp?.beneficiaryConfirmationEnabled;
  }
  return !!settings?.authentication?.inApp?.beneficiaryConfirmationEnabled;
}

/**
 * Operations whose native flows require a user confirmation before the HTTP
 * call. Add an entry here (claim + settings flag) to gate a new operation;
 * the api-operations bridge awaits the runner before dispatching it.
 */
export const CONFIRMATION_GATED_OPERATIONS: Readonly<Record<string, ConfirmationGatedOperation>> = {
  // Mirrors submitUnifiedPayment: gate on smsAuthenticationEnabled, confirm
  // the makePayment claim (matches the POST /payments ConfirmationGuard).
  'payments.initiate': {
    claimType: ConfirmationClaimType.MAKE_PAYMENT,
    isConfirmationRequired: (store) =>
      !!store.selectSnapshot(ProjectSettingsState.getProjectSettings)?.smsAuthenticationEnabled,
  },
  // savings.internalTransfer is deliberately NOT gated: moving money between
  // the customer's own accounts is not a third-party payment (James,
  // 2026-07-30). The backend route still carries a MAKE_PAYMENT
  // ConfirmationGuard, which passes because the guard is permissive and no
  // token is sent — same as the native savings flow.
  // Mirrors the native domestic flow (addEditBeneficiary /
  // beneficiaryRequiresConfirmation): claim ADD_BENEFICIARY — the
  // international flow's ADD_INTERNATIONAL_BENEFICIARY claim belongs to a
  // separate flow the bridge does not expose — gated on the in-app
  // beneficiaryConfirmationEnabled flag with the persona-specific override.
  // Unlike payments.initiate, addBeneficiary attaches the confirmation token
  // (accessToken -> `confirmation` header), so the runner outcome is threaded
  // into the service call by the api-operations bridge.
  'beneficiaries.create': {
    claimType: ConfirmationClaimType.ADD_BENEFICIARY,
    isConfirmationRequired: isBeneficiaryConfirmationRequired,
  },
  // Same gate and same claim as the domestic operation, deliberately.
  // ConfirmationClaimType.ADD_INTERNATIONAL_BENEFICIARY exists, but the flow
  // that used it (callIntlBeneficiaryAPI / addInternationalBeneficiary in
  // cta-button-actions/intl-payments/) has no ButtonActionType and no call
  // site — it is dead code, it gated on smsAuthenticationEnabled, and it
  // attached no token. Its claim would also fail POST /beneficiaries, whose
  // ConfirmationGuard demands the literal claim `addBeneficiary`. The wired
  // flow (addEditBeneficiary) handles domestic and international alike:
  // ADD_BENEFICIARY + beneficiaryConfirmationEnabled, token attached.
  'beneficiaries.createInternational': {
    claimType: ConfirmationClaimType.ADD_BENEFICIARY,
    isConfirmationRequired: isBeneficiaryConfirmationRequired,
  },
  // Mirrors submitInternationalPayment: gate on smsAuthenticationEnabled (top
  // level only — the intl flow reads no persona override and no
  // smsAuthenticationExclusion) and confirm MAKE_PAYMENT, matching the POST
  // /payments ConfirmationGuard. Native attaches no token (confirmAction is
  // called with addTokenToUrl defaulted to false and transferMoneyInternational
  // sends no `confirmation` header), so neither does the bridge.
  'payments.initiateInternational': {
    claimType: ConfirmationClaimType.MAKE_PAYMENT,
    isConfirmationRequired: (store) =>
      !!store.selectSnapshot(ProjectSettingsState.getProjectSettings)?.smsAuthenticationEnabled,
  },
  // payments.precheckInternational is deliberately NOT gated: it is the same
  // POST /payments with review:true, a dry run that moves no money, and the
  // native precheck (internationPaymentPrecheck) runs no confirmation either.
};

/**
 * The production runner: OverlayConfirmationService renders the
 * confirmation-code flow (code entry, resend, 120s timeout) in a dialog over
 * the current page — no router navigation, so the widget iframe survives the
 * SCA and the suspended api.call resolves with the minted token.
 */
export function createOverlayConfirmationRunner(
  overlayConfirmationService: OverlayConfirmationService,
): ConfirmationRunner {
  return {
    run: async (claimType, operation) => {
      try {
        const { token } = await overlayConfirmationService.confirm(claimType, operation);
        return { token };
      } catch (error) {
        if (error instanceof ConfirmationDeclinedError) {
          throw new HostError('confirmation-declined', error.message);
        }
        throw error;
      }
    },
  };
}

/**
 * Fallback runner for hosts without the overlay service.
 *
 * The only confirmation implementation the platform has today is
 * ConfirmationService.confirmAction(), which does
 * router.navigate(['confirmation-code']) — a top-level primary-outlet route
 * (app-routing.module.ts) that replaces the whole zone page, destroying the
 * widget iframe mid-call. The 'modal' named outlet (PageAsModalComponent)
 * only hosts app-builder zone pages, so the confirmation UI cannot currently
 * run over the page. Until the platform can host ConfirmationCodeComponent
 * (or an equivalent) in an overlay, gated operations fail fast instead of
 * silently skipping SCA or killing the widget.
 */
export function createModalOutletConfirmationRunner(): ConfirmationRunner {
  return {
    run: async (claimType, operation) => {
      throw new HostError(
        'host-error',
        `confirmation-required-but-unsupported: '${operation}' requires a user confirmation ` +
          `(claim '${claimType}') but the platform confirmation UI is a full-page redirect ` +
          `(/confirmation-code) that would unmount the widget; an overlay-hosted confirmation ` +
          `is not available yet`,
      );
    },
  };
}
